import {__} from '@wordpress/i18n';
import {registerPaymentMethod} from '@woocommerce/blocks-registry';
import {getSetting} from '@woocommerce/settings';
import {decodeEntities} from '@wordpress/html-entities';
import {useCallback, useEffect, useRef} from '@wordpress/element';
import {type PaymentMethodInterface} from '@woocommerce/types';
import {type CheckoutAfterProcessingWithErrorEventData} from '@woocommerce/checkout';
import {type TokenResult, type Payments, type AfterpayClearpay, type PaymentRequest} from '@square/web-payments-sdk-types';
import {z} from '@peachpay/checkout-blocks';
import {getErrorString} from '../../../../../../frontend/@shared/ts/error';
import CurrencyFallback from '../../../../../blocks/src/currency-fallback';
import {buildOptions} from '../payment-request';
import {useSelect} from '@wordpress/data';
import {CART_STORE_KEY} from '@woocommerce/block-data';

const SettingsSchema = z.object({
	icon: z.string(),
	title: z.string(),
	description: z.string(),
	fallback_currency: z.string().nullable(),
	supports: z.string().array(),
	square: z.object({
		application_id: z.string(),
		location_id: z.string(),
		country_code: z.string(),
	}),
});

const parsedResult = SettingsSchema.safeParse(getSetting('peachpay_square_afterpay_data'));
if (!parsedResult.success) {
	throw new Error(`Payment method "peachpay_square_afterpay" setup aborted. Invalid settings.\n\n${parsedResult.error.message}`);
}

const settings = parsedResult.data;
const title = decodeEntities(settings.title);
const description = decodeEntities(settings.description);

const iconStyle = {maxHeight: '25px'} as const;
const descriptionStyle = {textAlign: 'left' as const, margin: '0.5rem 0 0'} as const;
const hrStyle = {margin: '0.5rem 0'} as const;
const smallTextStyle = {textAlign: 'left' as const, margin: '0', fontSize: 'smaller'} as const;
const afterpayMountContainerId = 'pp-square-afterpay-blocks-mount';
const afterpayMountStyle = {
	position: 'absolute' as const,
	width: '1px',
	height: '1px',
	overflow: 'hidden' as const,
	clipPath: 'inset(50%)',
} as const;

if (!window.Square) {
	throw new Error('Payment method "peachpay_square_afterpay" setup aborted. Square SDK not loaded.');
}

let payments: Payments;
try {
	payments = window.Square.payments(settings.square.application_id, settings.square.location_id);
} catch (err) {
	throw new Error(`Payment method "peachpay_square_afterpay" setup aborted. Could not create Payments instance.\n\n${getErrorString(err)}`);
}

type CartStore = {getCartData(): {billingAddress: {country: string}}};

const clearPayTitle = __('Clearpay', 'peachpay-for-woocommerce');
const afterPayTitle = __('Afterpay', 'peachpay-for-woocommerce');

/**
 * Component to render the payment method label
 */
function Label(props: PaymentMethodInterface) {
	const {PaymentMethodLabel, PaymentMethodIcons} = props.components;

	const cartData = useSelect(
		select => (select(CART_STORE_KEY) as unknown as CartStore).getCartData(),
		[],
	);

	const country = cartData?.billingAddress.country;
	const label = (title === afterPayTitle || title === clearPayTitle)
		? (country === 'GB' ? clearPayTitle : afterPayTitle)
		: title;

	return (
		<>
			<PaymentMethodLabel text={label} />
			<PaymentMethodIcons className='pp-label-icon' icons={[{
				id: 'peachpay_square_afterpay',
				src: settings.icon,
				alt: label,
			}]} />
		</>
	);
}

/**
 * Component to render the afterpay body.
 *
 * Afterpay uses a redirect-based flow: the buyer leaves the page, completes
 * payment on Afterpay's site, and returns. The Square SDK handles this via a
 * popup window internally. We must create both the PaymentRequest and the
 * AfterpayClearpay instance fresh inside onPaymentSetup so they always reflect
 * current cart totals and the instance is not stale from mount time.
 */
function Content({billing, cartData, eventRegistration, emitResponse}: PaymentMethodInterface) {
	const {
		onPaymentSetup,
		onCheckoutFail,
	} = eventRegistration;

	const containerRef = useRef<HTMLDivElement | null>(null);
	const afterpayRef = useRef<AfterpayClearpay | null>(null);

	const cleanupAfterpayInstance = useCallback(async () => {
		const previousAfterpay = afterpayRef.current;
		afterpayRef.current = null;

		if (!previousAfterpay) {
			return;
		}

		await previousAfterpay.destroy().catch(err => {
			console.error('[Square] Failed to destroy Afterpay instance:', err);
		});
	}, []);

	useEffect(() => () => {
		cleanupAfterpayInstance().catch(console.error);
	}, [cleanupAfterpayInstance]);

	/**
	 * Handle payment tokenization right before the order is submitted.
	 *
	 * A fresh PaymentRequest and AfterpayClearpay instance are created on every
	 * invocation so that the totals and currency are always current. This mirrors
	 * the ACH pattern where the ach() instance is also created inside the handler.
	 */
	useEffect(() => {
		const unsubscribe = onPaymentSetup(async () => {
			const mountContainer = containerRef.current;
			if (!mountContainer?.isConnected) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __('Afterpay failed to initialize. Please refresh and try again or use a different payment method.', 'peachpay-for-woocommerce'),
				};
			}

			await cleanupAfterpayInstance();

			let paymentRequest: PaymentRequest;
			try {
				paymentRequest = payments.paymentRequest(buildOptions({
					merchantCountry: settings.square.country_code,
					currency: billing.currency.code,
					items: cartData.cartItems,
					summary: billing.cartTotalItems,
					total: billing.cartTotal,
					displayPricesIncludingTax: billing.displayPricesIncludingTax,
				}));
			} catch (err) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: getErrorString(err),
				};
			}

			let tokenResult: TokenResult;
			try {
				const afterpay = await payments.afterpayClearpay(paymentRequest);
				afterpayRef.current = afterpay;
				await afterpay.attach(`#${afterpayMountContainerId}`);
				tokenResult = await afterpay.tokenize();
			} catch (err) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: getErrorString(err),
				};
			} finally {
				await cleanupAfterpayInstance();
			}

			if (tokenResult.status === 'Cancel' || tokenResult.status === 'Abort') {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: __('Payment was cancelled', 'peachpay-for-woocommerce'),
				};
			}

			if (tokenResult.status !== 'OK' || !tokenResult.token) {
				return {
					type: emitResponse.responseTypes.ERROR,
					message: ((tokenResult.errors ?? []) as Array<{message: string}>).map(err => err.message).join('<br/>')
						|| __('Afterpay tokenization failed. Please try again or use a different payment method.', 'peachpay-for-woocommerce'),
				};
			}

			return {
				type: emitResponse.responseTypes.SUCCESS,
				meta: {
					paymentMethodData: {
						peachpay_square_source_id: tokenResult.token,
					},
				},
			};
		});
		return unsubscribe;
	}, [
		billing.cartTotal.label,
		billing.cartTotal.value,
		billing.cartTotalItems,
		billing.currency.code,
		billing.displayPricesIncludingTax,
		cartData.cartItems,
		cleanupAfterpayInstance,
		onPaymentSetup,
	]);

	/**
	 * Handle checkout failure after the order was submitted. Failure may or may
	 * not be payment related.
	 */
	useEffect(() => {
		const unsubscribe = onCheckoutFail(async (data: CheckoutAfterProcessingWithErrorEventData) => {
			await cleanupAfterpayInstance();

			return {
				type: emitResponse.responseTypes.ERROR,
				message: (data.processingResponse?.paymentDetails?.['message']) ?? __('An unknown error occured while processing the payment.', 'peachpay-for-woocommerce'),
				messageContext: emitResponse.noticeContexts.PAYMENTS,
			};
		});
		return unsubscribe;
	}, [cleanupAfterpayInstance, onCheckoutFail]);

	/**
	 * If the payment method does not support the current currency we will show
	 * a fallback button to allow changing the currency.
	 *
	 * Note: Due to how our currency switcher works the `fallback_currency` setting
	 * is only defined when the payment does not support the current currency and
	 * does not change after page load.
	 */
	if (settings.fallback_currency) {
		return <CurrencyFallback name={title} currency={settings.fallback_currency} />;
	}

	return (
		<>
			<div id={afterpayMountContainerId} ref={containerRef} style={afterpayMountStyle} aria-hidden='true' />
			<img style={iconStyle} src={settings.icon} alt=''></img>
			<p style={descriptionStyle}>{title} {__('selected for checkout.', 'peachpay-for-woocommerce')}</p>
			<hr style={hrStyle}/>
			<p style={smallTextStyle}>{description}</p>
		</>
	);
}

registerPaymentMethod({
	name: 'peachpay_square_afterpay',
	ariaLabel: title,
	canMakePayment(data) {
		return data.paymentMethods.includes('peachpay_square_afterpay');
	},
	// @ts-expect-error WC will clone these components and pass the props when used.
	label: <Label />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	content: <Content />,
	// @ts-expect-error WC will clone these components and pass the props when used.
	edit: <Content />,
	supports: {
		features: settings.supports,
	},
});
